/* eslint-disable no-shadow */
import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import mapboxgl from 'mapbox-gl';
import { getToolTipDom } from './tooltipDom';
import { getMapboxToken } from '../../utils/functions';
import slices from '../../state/slices/data';

const MAPBOX_ACCESS_TOKEN = getMapboxToken();

export default function Map() {
  const geoJson = useSelector((state) => state.data.geoJson);
  const curIndex = useSelector((state) => state.data.curIndex);
  const dispatch = useDispatch();

  const { setIndex } = slices.actions;

  const mapContainer = useRef(null);
  const markersRef = useRef([]);
  const popupTimeoutRef = useRef(null);

  const popup = useRef(
    new mapboxgl.Popup({
      className: 'elec-popup',
      closeButton: true,
      closeOnClick: true,
      offset: [0, -10],
    }),
  );

  const [map, setMap] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

  useEffect(() => {
    if (map) return;

    const mapInstance = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/sbsonline/cmmvdaqpl000q01sm6l8qc8l0',
      center: [6.041746114619889, 46.45156497826319],
      zoom: 3,
      minZoom: 2,
      maxZoom: 10,
      pitch: 0,
      projection: 'mercator',
      bearing: 0,
      cooperativeGestures: true,
    });

    mapInstance.addControl(
      new mapboxgl.NavigationControl({
        showCompass: false,
      }),
      'top-left',
    );

    mapInstance.dragRotate.disable();
    mapInstance.touchZoomRotate.disableRotation();

    mapInstance.on('load', () => {
      setMap(mapInstance);
      setMapLoaded(true);
    });
  }, [map]);

  // Create markers once geojson is ready
  useEffect(() => {
    if (!mapLoaded || !map || !geoJson?.features?.length) return;

    // remove existing markers first
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];

    geoJson.features.forEach((feature) => {
      const coords = feature.geometry.coordinates;

      const marker = new mapboxgl.Marker({
        color: '#333',
      })
        .setLngLat(coords)
        .addTo(map);

      marker.getElement().addEventListener('click', () => {
        popup.current.remove();
        if (popupTimeoutRef.current) clearTimeout(popupTimeoutRef.current);
        dispatch(setIndex(feature.properties.id));
      });

      markersRef.current.push(marker);
    });
  }, [mapLoaded, map, geoJson, dispatch, setIndex]);

  // React to curIndex changes from marker click OR carousel click
  useEffect(() => {
    if (!mapLoaded || !map || !geoJson?.features?.length || curIndex == null)
      return;

    const activeFeature = geoJson.features.find(
      (feature) => String(feature.properties.id) === String(curIndex),
    );

    if (!activeFeature) return;

    const coords = activeFeature.geometry.coordinates;

    // close any existing popup immediately
    popup.current.remove();
    if (popupTimeoutRef.current) clearTimeout(popupTimeoutRef.current);

    // update marker styles
    markersRef.current.forEach((marker, i) => {
      const feature = geoJson.features[i];
      const el = marker.getElement();

      if (!feature) return;

      if (String(feature.properties.id) === String(curIndex)) {
        el.style.transform = 'scale(1.35)';
        el.style.zIndex = '10';
        el.style.filter = 'brightness(1.25)';
      } else {
        el.style.transform = 'scale(1)';
        el.style.zIndex = '1';
        el.style.filter = 'none';
      }
    });

    map.flyTo({
      center: coords,
      duration: 800,
      essential: true,
    });

    popupTimeoutRef.current = window.setTimeout(() => {
      popup.current
        .setLngLat(coords)
        .setHTML(getToolTipDom(activeFeature.properties))
        .setMaxWidth('320px')
        .addTo(map);
    }, 500);
  }, [mapLoaded, map, geoJson, curIndex]);

  return (
    <div id="map-wrap">
      <div ref={mapContainer} className="mapContainer" />
    </div>
  );
}
